import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import EntityForm from "../EntityForm/EntityForm";
import DualListBoxGroup from "../Common/DualListBoxGroup";
import CheckBoxGroup from "../Common/CheckBoxGroup";
import CONSTANTS from "../constants";
import { APP_COLORS } from "../../Common/constants";
import { logSafeError } from "../../../../lib/safeLog";
import "./customStyles.css";
import { usePropertyConfiguration } from "../../../../components/property-configuration-provider";
import { useChainData } from "../../../../components/chain-data-provider";
import { usePropertyLocales } from "../../../../components/property-locale-provider";
import { FullWidthLoader } from "../../../../components/full-width-loader";
import { ContainerLoader } from "../Promotions/PromotionsForm";
import customAxios from "../../../../http/customAxios";
import { useConfig } from "../../../../lib/config";
import { defineMessages, useIntl, RawIntlProvider } from "react-intl";
import Swal from "sweetalert2";
import {
  normalizeEmailTemplate,
  buildAgrTemplateMap,
  isUseStandardTemplate,
  buildAgrReadonlyLink,
  isCartAbandonmentType,
  mapEntityToEmailTemplateFormValues,
  resolveDestinationType,
} from "./application/emailTemplateForm.helpers";
import { isAgrEmailTemplate } from "./domain/agr.helpers";
import {
  preferredModalMessages,
  ForcePreferredBody,
  RemovePreferredBody,
  MarkAsDefaultBody,
} from "./preferredModals";
import {
  getAgrTemplates,
  createAgrTemplates,
  updateAgrTemplates,
  fetchAssignedEngines,
  getPropertyTemplates,
} from "./infrastructure/emailTemplateAgr.api";

// Notice/error banner (shared by the read-only notice and the AGR save-error message).
const Banner = styled.div`
  margin-bottom: 16px;
  padding: 10px 14px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  color: #721c24;
  font-size: 13px;
`;

// Governed fields that the aggregator owns; every one of these is locked on a child engine
// when the template is aggregator-shared. `preferred` is intentionally NOT in this list —
// it is the sole field the child may edit (B3-2).
const AGR_READONLY_FIELDS = [
  "type",
  "emailDestinationType",
  "isActive",
  "active",
  "templateType",
  "delay",
  "delayCartAbandonment",
  "delayCheckin",
  "delayCheckout",
  "chronoUnit",
  "emailGuestType",
  "singleMailOnMultipleRoomStays",
  "subject",
  "cartAbandonmentSubject",
  "paymentFailedSubject",
  "abandonedPaymentSubject",
  "upsellSubject",
  "quotationSubject",
  "waitingListSubject",
  "welcomeLoyaltySubject",
  "agenciesSubject",
  "body",
  "header",
  "footer",
];

const messages = defineMessages({
  privacyPolicyMissing: {
    id: "emailConfiguration.form.privacyPolicyMissing",
    description: "Privacy Policy is not configured error message",
    defaultMessage:
      "To activate cart abandonment emails, you must first configure a Privacy Policy in the Legal Info section.",
  },
  templateSyntaxError: {
    id: "emailConfiguration.form.templateSyntaxError",
    description: "Template syntax error message",
    defaultMessage:
      "There was a syntax error in the template. Please review the template content for any syntax issues.",
  },
  agrMinOneMotorError: {
    id: "emailConfiguration.form.agrMinOneMotorError",
    description: "AGR at least one motor required",
    defaultMessage: "At least one child motor must be selected.",
  },
  agrSaveError: {
    id: "emailConfiguration.form.agrSaveError",
    description: "AGR generic save error",
    defaultMessage: "An error occurred while saving the template. Please try again.",
  },
  // Reuse the shared property-selector labels (already translated in all locales) instead of
  // duplicating them under emailConfiguration.form.*.
  motorSelectorAvailable: {
    id: "propertySelector.available",
    description: "Available motors header",
    defaultMessage: "Available ({count})",
  },
  motorSelectorSelected: {
    id: "propertySelector.selected",
    description: "Selected motors header",
    defaultMessage: "Selected ({count})",
  },
  agrReadonlyMessage: {
    id: "emailConfiguration.form.agrReadonlyMessage",
    description: "Read-only notice for aggregator-shared templates on a child engine",
    defaultMessage:
      "This email template is managed from the aggregator engine. Only the preferred flag can be changed here.",
  },
  agrMissingContentLocales: {
    id: "emailConfiguration.form.agrMissingContentLocales",
    description: "AGR custom content missing for the aggregator's configured languages",
    defaultMessage: "Subject and body are required for the aggregator's languages: {locales}.",
  },
  agrReadonlyLink: {
    id: "emailConfiguration.form.agrReadonlyLink",
    description: "Link in the read-only banner to edit the template on the aggregator engine",
    defaultMessage: "Edit it on the aggregator engine",
  },
});

const EMPTY_ARRAY = [];

// sweetalert2 takes an HTMLElement, so the body is rendered into a detached node (same approach
// as the test-email modal). React gives us the rich-text copy and the collapsible engine list
// without ever building an HTML string out of backend-supplied tickers. The detached root is its
// own tree, so the intl context has to be re-supplied or FormattedMessage throws and renders
// nothing.
function mountModalBody(body, intl) {
  const container = document.createElement("div");
  const root = createRoot(container);
  root.render(<RawIntlProvider value={intl}>{body}</RawIntlProvider>);
  return { container, root };
}

// B2-1: In aggregator (chain) mode the Custom content editor must expose a tab for every
// language available to the aggregator, NOT the individual form's hardcoded set. This is a
// fixed superset (mirrors the Conditions/CancellationPolicy AGR forms, which use the same
// static list intentionally to avoid unioning each child motor's locales at runtime). Child
// engines use only their own active locales at send time (backend/property config); the
// frontend does not prune per child.
const AGR_SUPPORTED_LOCALES = [
  "es", "ca", "eu", "da", "de", "nl", "en", "fr", "hu", "it", "ja", "ko", "pt", "ro", "ru", "zh",
];

// Content fields whose per-locale presence is relaxed in aggregator mode (Option B): all 16
// languages are shown, but only the aggregator's configured locales are mandatory (enforced by
// a custom check in handleAgrSubmit), so the extra languages can be filled optionally.
const AGR_CONTENT_VALIDATION_FIELDS = [
  "subject", "cartAbandonmentSubject", "paymentFailedSubject", "abandonedPaymentSubject",
  "upsellSubject", "quotationSubject", "waitingListSubject", "welcomeLoyaltySubject",
  "agenciesSubject", "body", "header", "footer",
];

export function EmailTemplateForm({
  locales,
  entity,
  entityError,
  entityResult,
  loading,
  getListPath,
  entityName: entityNameProp,
  config,
  onSubmitEntity,
  hotelTicker,
}) {
  const { name, validation, fields: fieldsConfig, renderers, translation, entities } = config;
  const entityName = entityNameProp || config.entityName;
  const history = useHistory();
  const intl = useIntl();
  const configuration = useConfig();
  const propertyConfig = usePropertyConfiguration();
  const chainData = useChainData();
  const isChain = !!chainData?.isChain;
  const chainChildren = chainData?.children || EMPTY_ARRAY;

  // A shared template opened on an individual (child) engine is read-only except `preferred`.
  const isAgrLinkedInIndividual = useMemo(
    () => !isChain && isAgrEmailTemplate(entity || {}),
    [isChain, entity]
  );

  // Link to edit the parent template on the aggregator engine (shown in the read-only banner,
  // mirroring Conditions/CancellationPolicy). Needs the parent ticker + parent id from the DTO.
  const agrReadonlyLink = useMemo(() => {
    if (!entity?.parentPropertyTicker || entity?.parentId == null) {
      return null;
    }
    // Raw <a href> bypasses the router basename, so prefix with PUBLIC_URL (CRA sets it to the
    // `homepage`, i.e. "/v6/ng") — otherwise the link hits the domain root and 503s.
    const base = process.env.PUBLIC_URL || "";
    return `${base}${buildAgrReadonlyLink(entity.parentPropertyTicker, entity.parentId, entityName)}`;
  }, [entity, entityName]);

  const [isSubmit, setIsSubmit] = useState(false);
  const [fields, setFields] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(entityError);
  const [agrSaveError, setAgrSaveError] = useState(null);
  // Aggregator property-selector state (chain mode). The selection and the disabled set live in
  // component state so the dual list repaints as soon as the assigned-engines fetch resolves,
  // instead of round-tripping through EntityForm's internal formValues (which only refreshes on
  // entity remap or the next form interaction).
  const [selectedMotors, setSelectedMotors] = useState(EMPTY_ARRAY);
  const [disabledMotors, setDisabledMotors] = useState(EMPTY_ARRAY);
  const [isPreferred, setIsPreferred] = useState(false);
  // Email type + recipient currently chosen in the form; drive the assigned-engines query.
  const [selectedType, setSelectedType] = useState(
    entity?.type || CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_CONFIRMATION
  );
  const [selectedDestination, setSelectedDestination] = useState(
    entity?.emailDestinationType || CONSTANTS.ENTITY.EMAIL_TEMPLATE.DESTINATION_TYPES.ALL
  );
  const agrErrorRef = useRef(null);

  const showAdvancedEmailsManager = useMemo(
    () => propertyConfig && propertyConfig.showAdvancedEmailsManager === "1",
    [propertyConfig]
  );

  useEffect(() => {
    if (agrSaveError && agrErrorRef.current) {
      agrErrorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [agrSaveError]);

  useEffect(() => {
    if (propertyConfig) {
      if (!showAdvancedEmailsManager) {
        const fieldType = fieldsConfig.form.find(fieldForm => fieldForm.fieldName === "type");
        const newOptions = fieldType && fieldType.optionDisplay;
        delete newOptions["POST_STAY"];
        delete newOptions["PRE_STAY"];
        fieldsConfig.form.find(
          fieldForm => fieldForm.fieldName === "type" && (fieldForm.optionDisplay = newOptions)
        );
        fieldsConfig.form = fieldsConfig.form.filter(
          fieldForm => fieldForm.fieldName !== "useStandardTemplate"
        );
      }
      setFields(fieldsConfig);
      if (isLoading) {
        setIsLoading(false);
      }
    }
  }, [config, fieldsConfig, isLoading, propertyConfig, showAdvancedEmailsManager]);

  // On a shared template opened in an individual engine, disable every governed field
  // (`AGR_READONLY_FIELDS`). `preferred` stays enabled — it is the ONLY editable control on a
  // shared template. Mirrors CancellationPolicyForm's per-field `disabled` computedFields.
  const formFields = useMemo(() => {
    const base = fields && fields.form;
    if (!isAgrLinkedInIndividual || !base) {
      return base;
    }
    return base.map(field => {
      if (field.fieldName === "preferred") {
        // Force `preferred` visible here (component state is reliable) rather than relying on
        // the `_isAgrInIndividual` formValues flag, which isn't guaranteed to be set when the
        // field's own `visible` runs (entity loads async).
        return { ...field, visible: () => true, disabled: false };
      }
      if (AGR_READONLY_FIELDS.includes(field.fieldName)) {
        return { ...field, disabled: true };
      }
      return field;
    });
  }, [fields, isAgrLinkedInIndividual]);

  // B2-1: aggregator mode renders one translation tab per aggregator language (fixed
  // superset); individual mode keeps its existing locale set. Driving `locales` here also
  // drives how many entries land in `formValues.translations`, which is what the per-locale
  // mandatory Subject/Body validation (B2-2) iterates over.
  const formLocales = useMemo(
    () => (isChain ? AGR_SUPPORTED_LOCALES : locales),
    [isChain, locales]
  );

  // Option B (B2-1 + refinement): show all aggregator languages, but only REQUIRE Subject+Body
  // for the aggregator's actually-configured locales. Children cannot edit shared templates, so
  // the aggregator may optionally fill extra languages to avoid leaving a child on fallback.
  const propertyLocales = usePropertyLocales();
  const requiredLocales = useMemo(() => {
    if (!isChain) return locales;
    const configured = (propertyLocales || []).filter(l => AGR_SUPPORTED_LOCALES.includes(l));
    // Fallback: if the aggregator's locales cannot be resolved, require all shown (strict).
    return configured.length ? configured : AGR_SUPPORTED_LOCALES;
  }, [isChain, propertyLocales, locales]);

  // In aggregator mode, relax the generic per-tab presence for content fields so the 16 shown
  // languages are not all forced; the mandatory subset (requiredLocales) is enforced in
  // handleAgrSubmit. Individual mode keeps the original validation untouched.
  const formValidation = useMemo(() => {
    if (!isChain) return validation;
    const relaxed = { ...validation };
    AGR_CONTENT_VALIDATION_FIELDS.forEach(field => {
      if (relaxed[field]?.constraints?.presence) {
        relaxed[field] = {
          ...relaxed[field],
          constraints: {
            ...relaxed[field].constraints,
            presence: { ...relaxed[field].constraints.presence, allowEmpty: true },
          },
        };
      }
    });
    return relaxed;
  }, [isChain, validation]);

  const hasPrivacyPolicyConfigured = useCallback(async (ticker) => {
    const url = `${configuration.apiEndpoint}/api/legal/${ticker}/legalInfo`;
    const legalInfoList = await customAxios.get(url, { headers: { "x-property": ticker } });
    return legalInfoList.data.some(({ property }) => property === "PRIVACY_POLICY");
  }, [configuration.apiEndpoint]);

  // In aggregator mode, recompute the disabled (already-assigned) engines whenever the
  // email type or the recipient (emailDestinationType) changes.
  const refreshAssignedEngines = useCallback(
    (type, destination, excludeId, signal) => {
      if (!isChain || !type || !destination) {
        setDisabledMotors(EMPTY_ARRAY);
        return;
      }
      const baseURL = config.restConfig?.baseURL || "";
      fetchAssignedEngines(baseURL, hotelTicker, { type, destination, excludeId }, signal)
        .then(assigned => setDisabledMotors(assigned || EMPTY_ARRAY))
        .catch(err => {
          if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
            logSafeError("Could not fetch assigned engines", err);
            setDisabledMotors(EMPTY_ARRAY);
          }
        });
    },
    [isChain, hotelTicker, config.restConfig]
  );

  // Recompute the disabled (already-assigned) engines whenever the aggregator, the selected email
  // type or the recipient changes. Reactive to component state so the selector repaints as soon
  // as the fetch resolves.
  useEffect(() => {
    if (!isChain) {
      setDisabledMotors(EMPTY_ARRAY);
      return undefined;
    }
    const controller = new AbortController();
    const excludeId = entity?.id ? Number(entity.id) : undefined;
    // Guest-only types force GUEST at save (resolveDestinationType); query the same resolved
    // destination so the disabled-engines check matches what will be persisted (avoids a 409
    // surfacing only on save because the query used the raw ALL default).
    const queryDestination = resolveDestinationType({
      type: selectedType,
      emailDestinationType: selectedDestination,
    });
    refreshAssignedEngines(selectedType, queryDestination, excludeId, controller.signal);
    return () => controller.abort();
  }, [isChain, selectedType, selectedDestination, entity?.id, refreshAssignedEngines]);

  // Seed the tracked type/recipient from a (possibly async-loaded) entity on edit.
  useEffect(() => {
    if (entity?.type) setSelectedType(entity.type);
    if (entity?.emailDestinationType) setSelectedDestination(entity.emailDestinationType);
  }, [entity?.type, entity?.emailDestinationType]);

  // `preferred` is held here rather than in EntityForm's form values because cancelling the
  // unmark modal has to restore the checkbox, and the parent cannot reset EntityForm's state.
  useEffect(() => {
    setIsPreferred(!!entity?.preferred);
  }, [entity?.preferred]);

  // On aggregator chain EDIT, the standard entity fetch does NOT include `childPropertyTickers`,
  // so the property selector would show none selected. Fetch the aggregator map to resolve the
  // parent's assigned children and pre-select them in the dual list (mirrors ConditionsForm).
  const [agrChildTickers, setAgrChildTickers] = useState(undefined);
  // Children that currently hold this template as their default; listed in the unmark modal.
  const [preferredChildTickers, setPreferredChildTickers] = useState(EMPTY_ARRAY);
  useEffect(() => {
    if (!isChain || !entity?.id) {
      setAgrChildTickers(undefined);
      setPreferredChildTickers(EMPTY_ARRAY);
      return undefined;
    }
    const controller = new AbortController();
    const baseURL = config.restConfig?.baseURL || "";
    getAgrTemplates(baseURL, hotelTicker, Number(entity.id), controller.signal)
      .then(map => {
        const parentEntry = map?.[hotelTicker];
        setAgrChildTickers(
          Array.isArray(parentEntry?.childPropertyTickers) ? parentEntry.childPropertyTickers : []
        );
        setPreferredChildTickers(
          Object.keys(map || {}).filter(ticker => ticker !== hotelTicker && map[ticker]?.preferred)
        );
      })
      .catch(err => {
        if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
          setAgrChildTickers([]);
          setPreferredChildTickers(EMPTY_ARRAY);
        }
      });
    return () => controller.abort();
  }, [isChain, entity?.id, hotelTicker, config.restConfig]);

  // Seed the selection from the aggregator's currently-assigned children (chain edit), keeping
  // only tickers that are still actual children of the chain.
  useEffect(() => {
    if (Array.isArray(agrChildTickers)) {
      setSelectedMotors(agrChildTickers.filter(t => chainChildren.includes(t)));
    }
  }, [agrChildTickers, chainChildren]);

  // Track the selected email type / recipient so the assigned-engines query stays in sync.
  const handleAfterChange = useCallback(
    ({ field, newState }) => {
      if (!isChain) return;
      // EntityForm passes `field` as the field-name string (not an object).
      if (field !== "type" && field !== "emailDestinationType") return;
      const formValues = newState?.formValues || {};
      setSelectedType(formValues.type);
      setSelectedDestination(formValues.emailDestinationType);
    },
    [isChain]
  );

  const customFormValuesMapper = useCallback(
    ({ entity: ent, fields: flds, locales: locs, translationConfig }) => {
      const formValues = mapEntityToEmailTemplateFormValues({
        entity: ent,
        fields: flds,
        locales: locs,
        translationConfig,
      });
      // Drives the visibility of the property selector (chain) and the `preferred` field
      // (shared template on a child); the selection itself lives in component state.
      formValues._isChain = isChain;
      formValues._isAgrInIndividual = isAgrLinkedInIndividual;
      return formValues;
    },
    [isChain, isAgrLinkedInIndividual]
  );

  // In aggregator mode, render the property selector ourselves so it reads the disabled set and
  // the selection from component state (reactive) rather than the ModuleDefinition transform,
  // which relied on a stale formValues injection.
  const mergedRenderers = useMemo(
    () => ({
      ...(renderers?.form || {}),
      ...(isChain && {
        selectedMotors: () => {
          const availableCount = Math.max(0, chainChildren.length - selectedMotors.length);
          const field = {
            fieldName: "selectedMotors",
            options: chainChildren.map(ticker => ({
              label: ticker,
              value: ticker,
              disabled: disabledMotors.includes(ticker),
            })),
            leftListHeader: intl.formatMessage(messages.motorSelectorAvailable, {
              count: availableCount,
            }),
            rightListHeader: intl.formatMessage(messages.motorSelectorSelected, {
              count: selectedMotors.length,
            }),
          };
          return (
            <DualListBoxGroup
              value={selectedMotors}
              onChange={({ value }) => setSelectedMotors(value)}
              field={field}
            />
          );
        },
      }),
      // Custom-rendered so Cancel on the unmark modal can restore the check; the fieldset keeps
      // the spacing WidgetRenderer would have added.
      preferred: ({ field }) => (
        <fieldset>
          <CheckBoxGroup
            field={field}
            value={isPreferred}
            onChange={() => setIsPreferred(current => !current)}
          />
        </fieldset>
      ),
      // Hint under the `preferred` checkbox (shown only while inactive, gated by the field's
      // `visible`). Custom-rendered so it skips the fieldset wrapper and we control the spacing:
      // ~12px from the checkbox above, 24px to the "Property selection" section below.
      preferredInactiveHint: ({ field }) => (
        <div
          className="help-block text-neutral-500"
          style={{ marginTop: "-4px", marginBottom: "24px" }}>
          {field.value}
        </div>
      ),
    }),
    [renderers, isChain, chainChildren, disabledMotors, selectedMotors, intl, isPreferred]
  );

  // Maps the 3-option preferred modal to forcePreferred (Q4).
  // Returns true (Replace-for-all), false (Respect-individual), or null (Cancel → no request).
  const promptForcePreferred = useCallback(async () => {
    const { container, root } = mountModalBody(<ForcePreferredBody />, intl);
    const result = await Swal.fire({
      html: container,
      width: 560,
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: intl.formatMessage(preferredModalMessages.forcePreferredReplaceAll),
      denyButtonText: intl.formatMessage(preferredModalMessages.forcePreferredRespectIndividual),
      cancelButtonText: intl.formatMessage({ id: "app.cancel", defaultMessage: "Cancel" }),
      confirmButtonColor: APP_COLORS.primary,
      // Enter must not fire "replace on every engine".
      focusConfirm: false,
      customClass: { popup: "wbk-preferredTemplateModal wbk-preferredTemplateModal--threeActions" },
    });
    root.unmount();
    if (result.isConfirmed) return true;
    if (result.isDenied) return false;
    return null;
  }, [intl]);

  // Unmarking the default on the aggregator. Returns true (clear it on every selected engine,
  // `forcePreferred` overwrites the children), false (only the aggregator loses it, each child
  // keeps its own) or null (cancel → nothing is saved and the checkbox is restored).
  const promptRemovePreferred = useCallback(async () => {
    const motors = preferredChildTickers.filter(ticker => selectedMotors.includes(ticker));
    const { container, root } = mountModalBody(<RemovePreferredBody motors={motors} />, intl);
    const result = await Swal.fire({
      html: container,
      width: 560,
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: intl.formatMessage(preferredModalMessages.removePreferredRemoveAll),
      denyButtonText: intl.formatMessage(preferredModalMessages.removePreferredKeepIndividual),
      cancelButtonText: intl.formatMessage({ id: "app.cancel", defaultMessage: "Cancel" }),
      confirmButtonColor: APP_COLORS.primary,
      focusConfirm: false,
      customClass: { popup: "wbk-preferredTemplateModal wbk-preferredTemplateModal--threeActions" },
    });
    root.unmount();
    if (result.isConfirmed) return true;
    if (result.isDenied) return false;
    return null;
  }, [intl, preferredChildTickers, selectedMotors]);

  // Templates in this engine that already hold the default for the same type/recipient. An engine
  // allows only one, so whichever template is being saved displaces the other; backend performs
  // the handover and the caller decides which origin it is competing against.
  const fetchExistingDefaults = useCallback(
    async dto => {
      try {
        const baseURL = config.restConfig?.baseURL || "";
        const templates = await getPropertyTemplates(baseURL, hotelTicker);
        return templates.filter(
          template =>
            template.preferred &&
            template.type === dto.type &&
            template.emailDestinationType === dto.emailDestinationType
        );
      } catch (err) {
        // Don't block the save on a failed lookup; the warning is a courtesy, backend owns the rule.
        logSafeError("Could not check the engine's preferred templates", err);
        return EMPTY_ARRAY;
      }
    },
    [config.restConfig, hotelTicker]
  );

  // Both directions share the layout; only the sentence changes.
  const promptOverridePreferred = useCallback(
    async message => {
      const { container, root } = mountModalBody(<MarkAsDefaultBody message={message} />, intl);
      const result = await Swal.fire({
        html: container,
        width: 560,
        showCancelButton: true,
        confirmButtonText: intl.formatMessage(preferredModalMessages.overridePreferredAccept),
        cancelButtonText: intl.formatMessage({ id: "app.cancel", defaultMessage: "Cancel" }),
        confirmButtonColor: APP_COLORS.primary,
        focusConfirm: false,
        customClass: { popup: "wbk-preferredTemplateModal" },
      });
      root.unmount();
      return result.isConfirmed;
    },
    [intl]
  );

  const parseAgrError = useCallback((err) => {
    const data = err?.response?.data;
    const text =
      typeof data === "string"
        ? data
        : typeof data?.message === "string"
        ? data.message
        : "";
    const trimmed = text.trim();
    if (trimmed.length > 0) {
      // Map a Handlebars/template validation error to the friendly message, same as the
      // individual path. B2-3: the exact aggregator error text is pending backend confirmation,
      // so this is a best-effort match on the same pattern; otherwise show the backend text.
      if (/Validation failed/i.test(trimmed)) {
        return intl.formatMessage(messages.templateSyntaxError);
      }
      return trimmed;
    }
    return intl.formatMessage(messages.agrSaveError);
  }, [intl]);

  const handleAgrSubmit = useCallback(
    async ({ formValues, dto }) => {
      if (selectedMotors.length === 0) {
        setAgrSaveError(intl.formatMessage(messages.agrMinOneMotorError));
        return;
      }

      // Option B: for a Custom template, Subject+Body are mandatory only for the aggregator's
      // configured locales (requiredLocales); the extra shown languages are optional.
      if (!isUseStandardTemplate(formValues)) {
        const missing = requiredLocales.filter(loc => {
          const t = (dto.translations || []).find(x => x.locale === loc);
          return !t || !t.subject || !t.body;
        });
        if (missing.length > 0) {
          setAgrSaveError(
            intl.formatMessage(messages.agrMissingContentLocales, { locales: missing.join(", ") })
          );
          return;
        }
      }

      // Prompt for propagation ONLY when the `preferred` flag actually changed vs the persisted
      // value, in either direction (mark or unmark). Editing just translations/copys must not
      // trigger the modal, and unmarking must also be able to cascade to children (backend
      // propagates whatever `preferred` value we send when forcePreferred is true).
      let forcePreferred = false;
      const preferredChanged = !!formValues.preferred !== !!entity?.preferred;
      if (preferredChanged) {
        const decision = formValues.preferred
          ? await promptForcePreferred()
          : await promptRemovePreferred();
        if (decision === null) {
          // Cancel → send nothing and restore the checkbox to its persisted value.
          setIsPreferred(!!entity?.preferred);
          return;
        }
        forcePreferred = decision;
      }

      // Send a sensible descriptor (Q2/B1-1): the identifier the backend persists on the
      // parent copy. Never synthesize the `_agr` suffix — backend appends it on children.
      const descriptor = dto.descriptor || `${dto.type}_${dto.emailDestinationType}_${hotelTicker}`;
      // Drop unauthored locales (no body and no header/footer). The form shows all aggregator
      // languages but only the configured ones are filled; persisting the empty ones would leave
      // the parent and every child copy with empty translations instead of falling back to the
      // configured language at send time (custom-content spec). This filter applies ONLY to Custom
      // templates: a Default template of a body-based type carries subject-only translations (no
      // body/header/footer), so filtering would drop them all and send translations: [] — which the
      // backend rejects (requires at least one translation).
      const authoredTranslations = isUseStandardTemplate(formValues)
        ? dto.translations || []
        : (dto.translations || []).filter(
            translation => translation.body || translation.header || translation.footer
          );
      const dtoWithPreferred = {
        ...dto,
        translations: authoredTranslations,
        descriptor,
        preferred: !!formValues.preferred,
      };
      const map = buildAgrTemplateMap(dtoWithPreferred, hotelTicker, selectedMotors);
      const baseURL = config.restConfig?.baseURL || "";

      setIsSubmit(true);
      setAgrSaveError(null);
      try {
        if (formValues.id) {
          await updateAgrTemplates(baseURL, hotelTicker, Number(formValues.id), map, forcePreferred);
        } else {
          await createAgrTemplates(baseURL, hotelTicker, map, forcePreferred);
        }
        // POST/PUT return no body (B1-2). Navigate to the list, which refetches on mount
        // (RestEntityHOC.componentDidMount → fetchEntities, no cache), so the new/updated rows
        // show without a full page reload.
        history.push(getListPath({ hotelTicker, entityName, state: true }));
      } catch (err) {
        logSafeError("Error saving AGR email template", err);
        setAgrSaveError(parseAgrError(err));
      } finally {
        setIsSubmit(false);
      }
    },
    [intl, promptForcePreferred, promptRemovePreferred, hotelTicker, config.restConfig, entityName, getListPath, parseAgrError, history, requiredLocales, selectedMotors, entity]
  );

  const handleSubmit = useCallback(
    async ({ formValues }) => {
      setIsSubmit(true);
      setError(null);
      setAgrSaveError(null);

      if (isCartAbandonmentType(formValues.type) && !(await hasPrivacyPolicyConfigured(formValues.hotelTicker))) {
        setError({ localMessage: intl.formatMessage(messages.privacyPolicyMissing) });
        setIsSubmit(false);
        return;
      }

      // The `preferred` checkbox is controlled by this component, so fold its value back in
      // before building the DTO.
      const values = { ...formValues, preferred: isPreferred };

      // B1-8: single source of truth for entity construction (individual + aggregator).
      const normalized = normalizeEmailTemplate(values);

      if (isChain) {
        setIsSubmit(false);
        await handleAgrSubmit({ formValues: values, dto: normalized });
        return;
      }

      // Newly marked as default: the engine allows a single default per type/recipient, so confirm
      // the handover against whichever template currently holds it — the aggregator's copy when an
      // own template takes over, the own one when the shared copy does.
      if (isPreferred && !entity?.preferred) {
        const existingDefaults = await fetchExistingDefaults(normalized);
        const displaced = existingDefaults.find(
          template => isAgrEmailTemplate(template) !== isAgrLinkedInIndividual
        );
        if (displaced) {
          const accepted = await promptOverridePreferred(
            isAgrLinkedInIndividual
              ? preferredModalMessages.overrideOwnPreferredText
              : preferredModalMessages.overrideAgrPreferredText
          );
          if (!accepted) {
            setIsSubmit(false);
            return;
          }
        }
      }

      // Shared template edited on a child engine (B3-5): send the FULL DTO — preserve the
      // linkage fields so the backend recognises it as aggregator-linked and applies only
      // `preferred`. View mode never reaches here (the form has no submit button).
      const entityToSubmit = isAgrLinkedInIndividual
        ? {
            ...normalized,
            isLinkedToAggregator: true,
            parentPropertyTicker: entity?.parentPropertyTicker,
            parentId: entity?.parentId,
            descriptor: entity?.descriptor ?? normalized.descriptor,
          }
        : normalized;

      try {
        onSubmitEntity({ entity: entityToSubmit });
      } finally {
        setIsSubmit(false);
      }
    },
    [hasPrivacyPolicyConfigured, intl, onSubmitEntity, isChain, handleAgrSubmit, isAgrLinkedInIndividual, entity, fetchExistingDefaults, promptOverridePreferred, isPreferred]
  );

  const parseUnexpectedError = useCallback((err) => {
    if (err.message && err.message.message && err.message.message.match(/Validation failed/)) {
      return { localMessage: intl.formatMessage(messages.templateSyntaxError) }
    }
    return err;
  }, [intl]);

  useEffect(() => {
    if (entityError) {
      if (entityError.code === "UNEXPECTED") {
        setError(parseUnexpectedError(entityError));
      } else {
        setError(entityError);
      }
    }
  }, [entityError, parseUnexpectedError]);

  if (isLoading) {
    return (
      <ContainerLoader>
        <FullWidthLoader />
      </ContainerLoader>
    );
  }

  return (
    <div>
      {isAgrLinkedInIndividual && (
        <Banner>
          {intl.formatMessage(messages.agrReadonlyMessage)}
          {agrReadonlyLink && (
            <>
              {" "}
              <a href={agrReadonlyLink}>{intl.formatMessage(messages.agrReadonlyLink)}</a>
            </>
          )}
        </Banner>
      )}
      <EntityForm
        loading={loading || isSubmit}
        name={name}
        entity={entity}
        entities={entities}
        validation={formValidation}
        renderers={mergedRenderers}
        fields={formFields}
        locales={formLocales}
        translation={translation}
        onSubmit={handleSubmit}
        entityError={error}
        entityResult={entityResult}
        entityName={entityName}
        getListPath={getListPath}
        entityToFormValuesMapper={customFormValuesMapper}
        afterOnChange={handleAfterChange}
      />
      {agrSaveError && <Banner ref={agrErrorRef}>{agrSaveError}</Banner>}
    </div>
  );
}

EmailTemplateForm.propTypes = {};

EmailTemplateForm.defaultProps = {
  locales: ["es", "ca", "en", "fr", "it", "de"],
};

export default EmailTemplateForm;
